/**
 * Cliente de la API backend (NestJS).
 * Usa ruta relativa: el navegador llama al mismo origen y Next.js
 * reenvía /api/* a la API por la red interna (ver next.config.ts).
 */
function apiBase() {
  return "/api";
}

export class ApiError extends Error {
  status: number;
  body: { message?: string; confirmable?: boolean } & Record<string, unknown>;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = (body ?? {}) as ApiError["body"];
  }
}

function extractError(data: unknown, status: number): string {
  if (data && typeof data === "object" && "message" in data) {
    const m = (data as { message: unknown }).message;
    if (Array.isArray(m)) return m.join(", ");
    if (typeof m === "string") return m;
  }
  return `Error ${status}`;
}

// Interceptor global de sesión caducada: si una petición autenticada devuelve 401,
// la cookie httpOnly ha expirado (o no hay sesión). Como es httpOnly no podemos
// limpiarla desde JS, así que redirigimos al login (el guard del backend ya la
// ignora por expirada). Se excluyen las rutas /auth/* para que el formulario de
// login muestre su propio error y para no rebotar en /auth/me (no logueado = normal).
let redirigiendo = false;
function manejar401(path: string) {
  if (typeof window === "undefined") return; // SSR: nada que redirigir
  if (path.startsWith("/auth/")) return; // login/me/etc. gestionan su propio 401
  if (redirigiendo) return; // evita disparos múltiples de varias llamadas en paralelo
  const { pathname } = window.location;
  if (pathname === "/login") return; // ya estamos en el login → sin bucle
  redirigiendo = true;
  // Conservamos a dónde quería ir para volver tras reautenticarse.
  const destino = pathname + window.location.search;
  const next = destino && destino !== "/" ? `?next=${encodeURIComponent(destino)}` : "";
  window.location.assign(`/login${next}`);
}

// El token web va en una cookie httpOnly; `credentials: 'include'` la envía sola
// (mismo origen vía el proxy de Next). No se añade cabecera Authorization.
async function request<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(`${apiBase()}${path}`, {
    credentials: "include",
    ...init,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) manejar401(path);
    throw new ApiError(extractError(data, res.status), res.status, data);
  }
  return data as T;
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>(path, {});
}

export function apiPost<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function apiPut<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function apiDelete<T>(path: string): Promise<T> {
  return request<T>(path, { method: "DELETE" });
}

// Descarga un fichero binario (p. ej. un Excel) respetando la cookie de sesión.
// Toma el nombre del Content-Disposition si viene; si no, usa `fallbackName`.
export async function apiDownload(path: string, fallbackName: string, body?: unknown): Promise<void> {
  // Con `body`, por POST (p. ej. el Excel de una comparativa con las líneas visibles y su orden).
  const res = await fetch(
    `${apiBase()}${path}`,
    body === undefined
      ? { credentials: "include" }
      : { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
  );
  if (!res.ok) {
    if (res.status === 401) manejar401(path);
    const data = await res.json().catch(() => ({}));
    throw new ApiError(extractError(data, res.status), res.status, data);
  }
  const cd = res.headers.get("Content-Disposition") ?? "";
  // Primero el nombre UTF-8 (filename*=UTF-8''…), que conserva acentos y símbolos; si no, el ASCII.
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(cd)?.[1];
  const name = (utf8 && decodeURIComponent(utf8)) || /filename="?([^";]+)"?/.exec(cd)?.[1] || fallbackName;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---- Tipos de la API (ids bigint llegan como string) ----

export type Rol = "admin" | "ingenieria" | "compras" | "produccion" | "lectura";
export const ROLES: Rol[] = ["admin", "ingenieria", "compras", "produccion", "lectura"];

export type EstadoVersion = "borrador" | "activa" | "no_recomendada" | "obsoleta";
export type EstadoEcn = "abierto" | "cerrado" | "cancelado";

export type Paginado<T> = { items: T[]; total: number };

export type UsuarioCuenta = {
  id: string;
  email: string;
  nombre: string;
  roles: Rol[];
  estado: "activo" | "bloqueado";
  debe_cambiar_password: boolean;
  ultimo_login_en: string | null;
  creado_en: string;
};

export type Cliente = {
  id: string;
  nombre: string;
  codigo: string | null;
  notas: string | null;
  productos?: number;
};

export type Opcion = { id: string; nombre: string };

export type ProductoResumen = {
  id: string;
  codigo: string | null;
  nombre: string;
  descripcion: string | null;
  cliente_id: string | null;
  cliente_nombre: string | null;
  familia_nombre: string | null;
  responsable_nombre: string | null;
  variantes: number;
  versiones_activas: number;
  borradores: number;
  actualizado_en: string;
};

export type VersionResumen = {
  id: string;
  variante_id: string;
  nombre: string;
  estado: EstadoVersion;
  derivada_de_id: string | null;
  derivada_de_nombre: string | null;
  derivada_de_variante: string | null;
  publicada_en: string | null;
  creado_en: string;
  ecn_id: string | null;
  ecn_numero: string | null;
  ecn_estado: EstadoEcn | null;
  ecn_comentario: string | null;
  creado_por_nombre: string | null;
};

export type Variante = {
  id: string;
  nombre: string;
  descripcion: string | null;
  es_base: boolean;
  versiones: VersionResumen[];
};

export type Producto = {
  id: string;
  codigo: string | null;
  nombre: string;
  descripcion: string | null;
  cliente_id: string | null;
  cliente_nombre: string | null;
  familia_id: string | null;
  familia_nombre: string | null;
  responsable_id: string | null;
  responsable_nombre: string | null;
  creado_por_nombre: string | null;
  creado_en: string;
  actualizado_en: string;
  variantes: Variante[];
};

export type EcnCasilla = {
  id: string;
  grupo: "ambito" | "impacto";
  etiqueta: string;
  orden: number;
  activo: boolean;
};

export type CambiosVersion = {
  ficheros: { accion: string; nombre: string }[];
  documentos_offline: { accion: string; nombre: string }[];
  boms: { accion: string; nombre: string; detalle?: string; bom_antes?: string; bom_despues?: string }[];
  /** Solo en versiones de producto: cambios de estructura (conjuntos). */
  conjuntos?: { accion: string; nombre: string; detalle?: string; version_antes?: string; version_despues?: string }[];
};

export type LineaDiff = {
  designadores: string | null;
  mpn: string | null;
  fabricante: string | null;
  descripcion: string | null;
  valor: string | null;
  cantidad: number | null;
  dnp: boolean;
};
export type DiffBom = {
  anadidas: LineaDiff[];
  quitadas: LineaDiff[];
  cambiadas: { antes: LineaDiff; despues: LineaDiff }[];
};

export type Ecn = {
  id: string;
  numero: string;
  objeto: "producto" | "conjunto";
  conjunto_id: string | null;
  version_conjunto_nueva_id: string | null;
  tipo: "version" | "variante";
  estado: EstadoEcn;
  producto_id: string;
  producto_nombre: string;
  producto_codigo: string | null;
  version_origen_id: string;
  origen_nombre: string;
  origen_variante: string;
  origen_estado: EstadoVersion;
  version_nueva_id: string;
  nueva_nombre: string;
  nueva_variante: string;
  que_cambia: string;
  por_que: string;
  comentario: string | null;
  estado_anterior: Exclude<EstadoVersion, "borrador">;
  cambios: CambiosVersion | null;
  casillas: Pick<EcnCasilla, "id" | "grupo" | "etiqueta">[];
  autor: string | null;
  cerrado_por_nombre: string | null;
  creado_en: string;
  cerrado_en: string | null;
};

export type EcnResumen = {
  id: string;
  numero: string;
  objeto: "producto" | "conjunto";
  conjunto_id: string | null;
  tipo: "version" | "variante";
  estado: EstadoEcn;
  que_cambia: string;
  comentario: string | null;
  creado_en: string;
  cerrado_en: string | null;
  producto_id: string;
  producto_nombre: string;
  producto_codigo: string | null;
  origen_nombre: string;
  origen_variante: string;
  nueva_id: string;
  nueva_nombre: string;
  nueva_variante: string;
  autor: string | null;
};

export type VersionDetalle = {
  id: string;
  nombre: string;
  estado: EstadoVersion;
  notas: string | null;
  derivada_de_id: string | null;
  derivada_de_nombre: string | null;
  derivada_de_variante: string | null;
  variante_id: string;
  variante_nombre: string;
  variante_es_base: boolean;
  producto_id: string;
  producto_nombre: string;
  producto_codigo: string | null;
  cliente_nombre: string | null;
  publicada_en: string | null;
  publicada_por_nombre: string | null;
  creado_por_nombre: string | null;
  creado_en: string;
  derivadas: { id: string; nombre: string; estado: EstadoVersion; variante_nombre: string }[];
  ecn: Ecn | null;
};

export type Comentario = {
  id: string;
  texto: string;
  version_id: string | null;
  version_nombre: string | null;
  variante_nombre: string | null;
  autor_id: string;
  autor: string;
  creado_en: string;
};

/** Datos del formulario de ECN (POST /versiones y PATCH /ecn/:id). */
export type EcnDatos = {
  que_cambia: string;
  por_que: string;
  comentario: string;
  estado_anterior: Exclude<EstadoVersion, "borrador">;
  casillas: string[];
};

// ---- BOMs, ficheros, documentos offline, búsqueda ----

export type Ambito = { id: string; clave: string; nombre: string };
export type TipoDocumento = { id: string; clave: string; nombre: string; extensiones: string[] };

export const CAMPOS_BOM = [
  "designadores",
  "cantidad",
  "mpn",
  "fabricante",
  "descripcion",
  "valor",
  "encapsulado",
  "order_info",
  "proveedor",
  "dnp",
] as const;
export type CampoBom = (typeof CAMPOS_BOM)[number];
export type MapeoBom = Partial<Record<CampoBom, number | null>>;

export type HojaAnalizada = {
  nombre: string;
  total_filas: number;
  filas: string[][];
  fila_cabecera: number;
  mapeo: MapeoBom;
  plantilla: boolean;
};
export type AnalisisBom = { blob_id: string; fichero_nombre: string; hojas: HojaAnalizada[] };

export type BomEnlace = {
  bom_id: string;
  carpeta: string;
  nombre: string;
  cantidad_por_producto: number;
  ambito_id: string;
  ambito_clave: string;
  ambito_nombre: string;
  num_lineas: number;
  lineas_dnp: number;
  fichero_nombre: string | null;
  hoja: string | null;
  creado_en: string;
  creado_por_nombre: string | null;
  heredada: boolean;
  importada_en_version: string | null;
  importada_en_variante: string | null;
};

export type LineaBom = {
  id: string;
  posicion: number;
  designadores: string | null;
  cantidad: number | null;
  fabricante: string | null;
  mpn: string | null;
  descripcion: string | null;
  valor: string | null;
  encapsulado: string | null;
  proveedor: string | null;
  order_info: string | null;
  dnp: boolean;
  datos: Record<string, string>;
};

export type BomDetalle = {
  id: string;
  fichero_nombre: string | null;
  hoja: string | null;
  num_lineas: number;
  creado_en: string;
  creado_por_nombre: string | null;
  blob_id: string | null;
  lineas: LineaBom[];
};

export type LineaTotal = {
  mpn: string | null;
  fabricante: string | null;
  descripcion: string | null;
  valor: string | null;
  order_info: string | null;
  cantidad_por_producto: number;
  cantidad_total: number;
  boms: string;
  falta_cantidad: boolean;
};
export type ListaTotal = { unidades: number; ambito: string | null; lineas: LineaTotal[] };

export type FicheroVersion = {
  id: string;
  carpeta: string;
  nombre: string;
  descripcion: string | null;
  etiquetas: string[];
  bom_nombre: string | null;
  tamano: number;
  ambito_id: string;
  ambito_nombre: string;
  ambito_clave: string;
  tipo_documento_id: string;
  tipo_nombre: string;
  creado_en: string;
  creado_por_nombre: string | null;
  heredado: boolean;
};

export type DocumentoOffline = {
  id: string;
  carpeta: string;
  nombre: string;
  ruta: string | null;
  ubicacion: string | null;
  descripcion: string | null;
  etiquetas: string[];
  ambito_id: string;
  ambito_nombre: string;
  tipo_documento_id: string;
  tipo_nombre: string;
  creado_en: string;
  heredado: boolean;
};

export type VersionRef = { id: string; nombre: string; variante: string; estado: EstadoVersion };

export type UsoComponente = {
  linea_id: string;
  conjunto_id: string;
  conjunto_nombre: string;
  version_conjunto_id: string;
  version_conjunto_nombre: string;
  mpn: string | null;
  fabricante: string | null;
  descripcion: string | null;
  valor: string | null;
  designadores: string | null;
  cantidad: number | null;
  dnp: boolean;
  bom_nombre: string;
  bom_id: string;
  ambito_nombre: string;
  version_id: string;
  version_nombre: string;
  version_estado: EstadoVersion;
  variante_nombre: string;
  producto_id: string;
  producto_nombre: string;
  producto_codigo: string | null;
  cliente_nombre: string | null;
};

export type ResultadoBusqueda = {
  limite: number;
  productos: { id: string; codigo: string | null; nombre: string; cliente_nombre: string | null }[];
  conjuntos: { id: string; codigo: string | null; nombre: string; ambito_nombre: string; versiones: string | null }[];
  ficheros: (Pick<FicheroVersion, "id" | "carpeta" | "nombre" | "descripcion" | "etiquetas" | "ambito_nombre" | "tipo_nombre" | "tamano"> & {
    producto_id: string;
    producto_nombre: string;
    producto_codigo: string | null;
    cliente_nombre: string | null;
    conjuntos: string;
    versiones: VersionRef[];
  })[];
  documentos: (Pick<DocumentoOffline, "id" | "nombre" | "ruta" | "ubicacion" | "descripcion" | "ambito_nombre" | "tipo_nombre"> & {
    producto_id: string;
    producto_nombre: string;
    producto_codigo: string | null;
    cliente_nombre: string | null;
    conjuntos: string;
    versiones: VersionRef[];
  })[];
  lineas_bom: (Pick<UsoComponente, "mpn" | "fabricante" | "descripcion" | "valor" | "designadores" | "cantidad" | "bom_nombre" | "bom_id" | "version_id" | "version_nombre" | "version_estado" | "variante_nombre" | "producto_nombre" | "producto_codigo" | "conjunto_nombre" | "version_conjunto_nombre">)[];
  ecn: { id: string; numero: string; estado: EstadoEcn; comentario: string | null; que_cambia: string; version_nueva_id: string | null; version_conjunto_nueva_id: string | null; conjunto_id: string | null; producto_nombre: string }[];
  comentarios: { id: string; texto: string; creado_en: string; autor: string; producto_id: string; version_id: string | null; producto_nombre: string }[];
};

export type Comparacion = {
  a: { id: string; nombre: string; estado: EstadoVersion; variante_nombre: string; producto_id: string; producto_nombre: string };
  b: Comparacion["a"];
  cambios: CambiosVersion;
};

/** Sube un FormData con progreso (fetch no informa del progreso de subida; XHR sí). */
export function apiUpload<T>(
  path: string,
  form: FormData,
  onProgress?: (fraccion: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${apiBase()}${path}`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => {
      let data: unknown = {};
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        /* respuesta vacía */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as T);
      else {
        if (xhr.status === 401) manejar401(path);
        reject(new ApiError(extractError(data, xhr.status), xhr.status, data));
      }
    };
    xhr.onerror = () => reject(new ApiError("Error de red durante la subida", 0, {}));
    xhr.send(form);
  });
}

/** URL directa de una descarga de la API (la cookie de sesión va sola: mismo origen). */
export const urlApi = (path: string) => `${apiBase()}${path}`;

// ---- Estructura del producto (conjuntos con versión propia) ----

export type ItemEstructura = {
  id: string;
  padre_id: string | null;
  conjunto_id: string;
  conjunto_nombre: string;
  conjunto_codigo: string | null;
  conjunto_descripcion: string | null;
  ambito_id: string;
  ambito_nombre: string;
  version_conjunto_id: string;
  version_nombre: string;
  version_estado: EstadoVersion;
  version_derivada_de_id: string | null;
  version_derivada_de_nombre: string | null;
  /** Borrador automático aún sin cambios: al publicar seguirá en su versión de origen. */
  sin_cambios: boolean;
  ecn_id: string | null;
  ecn_numero: string | null;
  ecn_estado: EstadoEcn | null;
  cantidad: number;
  orden: number;
  elementos: number;
  ruta_ids: string;
  ruta_nombres: string;
  hijos: ItemEstructura[];
};

export type VersionConjuntoResumen = {
  id: string;
  nombre: string;
  estado: EstadoVersion;
  derivada_de_id: string | null;
  derivada_de_nombre: string | null;
  publicada_en: string | null;
  creado_en: string;
  ecn_id: string | null;
  ecn_numero: string | null;
  ecn_estado: EstadoEcn | null;
  ecn_comentario: string | null;
  elementos: number;
};

export type UsoConjunto = {
  producto_id: string;
  producto_nombre: string;
  producto_codigo: string | null;
  variante_nombre: string;
  version_id: string;
  version_nombre: string;
  version_estado: EstadoVersion;
  version_conjunto_nombre?: string;
};

export type ConjuntoDetalle = {
  id: string;
  codigo: string | null;
  nombre: string;
  descripcion: string | null;
  ambito_id: string;
  ambito_nombre: string;
  producto_origen_nombre: string | null;
  creado_por_nombre: string | null;
  creado_en: string;
  versiones: VersionConjuntoResumen[];
  usado_en: UsoConjunto[];
};

export type ConjuntoResumen = {
  id: string;
  codigo: string | null;
  nombre: string;
  descripcion: string | null;
  ambito_id: string;
  ambito_nombre: string;
  producto_origen_nombre: string | null;
  versiones: { id: string; nombre: string; estado: EstadoVersion }[] | null;
  productos: number;
};

export type VersionConjuntoDetalle = {
  id: string;
  conjunto_id: string;
  conjunto_nombre: string;
  conjunto_codigo: string | null;
  ambito_id: string;
  ambito_nombre: string;
  nombre: string;
  estado: EstadoVersion;
  notas: string | null;
  derivada_de_id: string | null;
  derivada_de_nombre: string | null;
  publicada_en: string | null;
  publicada_por_nombre: string | null;
  creado_por_nombre: string | null;
  creado_en: string;
  ecn: Ecn | null;
  usada_en: UsoConjunto[];
};

// ---- Compras ----

export type Distribuidor = {
  id: string;
  clave: string;
  nombre: string;
  activo: boolean;
  configurado: boolean;
  listo: boolean;
  carrito_api: boolean;
  campos: { clave: string; etiqueta: string; secreto: boolean; opcional?: boolean }[];
  ultima_prueba_ok: boolean | null;
  ultima_prueba_en: string | null;
  ultima_prueba_msg: string | null;
  actualizado_en: string | null;
};

export type OfertaEvaluada = {
  distribuidor: string;
  sku: string;
  mpn: string;
  fabricante: string | null;
  descripcion: string | null;
  url: string | null;
  stock: number | null;
  moq: number;
  multiplo: number;
  tramos: { cantidad: number; precio: number }[];
  moneda: string;
  empaquetado?: string | null;
  /** En unidades de venta (bolsas, si `piezas` > 1). */
  cantidad_pedir: number;
  /** Piezas que llegan (cantidad_pedir × piezas). Falta en comparativas antiguas. */
  piezas_pedidas?: number;
  /** Piezas por unidad de venta (bolsa de 100 → 100). */
  piezas?: number;
  precio_unitario: number;
  total: number;
  sin_stock: boolean;
  sugerencia?: { cantidad: number; total: number };
  /** Mismo MPN de otro fabricante que el de la BOM (copia): no se elige sola. */
  otro_fabricante?: boolean;
  /** Foto del componente en la web del distribuidor. */
  imagen?: string | null;
  /** Plazo de fábrica (días) y llegadas previstas al distribuidor (Mouser). */
  plazo_dias?: number | null;
  llegadas?: { cantidad: number; fecha: string }[];
  /** Ciclo de vida del fabricante según este distribuidor, y su texto tal cual. */
  ciclo_vida?: CicloVida;
  ciclo_vida_texto?: string | null;
  ultima_compra?: string | null;
  sustituto?: string | null;
  /** El distribuidor ya no lo vende. */
  descatalogado_distribuidor?: boolean;
  /** Precio de contrato (acuerdo con el distribuidor), no el público de catálogo. */
  precio_contrato?: boolean;
};

export type EstadoLineaCompra = "pendiente" | "ok" | "revisar" | "sin_stock" | "no_encontrado" | "error" | "excluida";

export type CicloVida = "activo" | "nrnd" | "ultima_compra" | "fin_de_vida" | "obsoleto";

export type CandidataGenerico = {
  mpn: string;
  fabricante: string | null;
  descripcion: string | null;
  distribuidor: string;
  stock: number | null;
  total: number;
  stock_suficiente: boolean;
  /** Lo que es de verdad: "25 V · C0G · ±1%" (falta en comparativas antiguas). */
  detalle?: string;
  /** Con qué se ha comprobado: "parámetros" (estructurados del distribuidor) o "descripción". */
  verificado?: string;
};

export type LineaComparativa = {
  id: string;
  posicion: number;
  mpn: string | null;
  fabricante: string | null;
  descripcion: string | null;
  consulta: string;
  por_texto: boolean;
  cantidad: number;
  boms: string | null;
  ofertas: OfertaEvaluada[];
  elegido_distribuidor: string | null;
  elegido_sku: string | null;
  elegido_manual: boolean;
  estado: EstadoLineaCompra;
  error: string | null;
  /** Lo necesario de verdad (`cantidad` = esto + cinta_extra si cinta_activa). */
  cantidad_base: number;
  encapsulado: string | null;
  encapsulado_fuente: string | null;
  /** Unidades de cinta para montaje (0 si no es chip estándar o la comparativa va sin cinta). */
  cinta_extra: number;
  cinta_activa: boolean;
  sobreprecio_cinta: number | null;
  /** Las unidades a comprar las ha fijado el usuario (no siguen a lo mínimo necesario). */
  cantidad_manual: boolean;
  /** Lo que ponía la BOM si era un condensador genérico ("1uF_0603_16V_X5R"); la referencia elegida va en `mpn`. */
  generico: string | null;
  /** Lo que se buscó, legible: "1 µF · 0603 · ≥16 V · X5R". */
  generico_espec: string | null;
  /** Referencias reales que cumplen (la primera es la elegida al comparar). */
  generico_candidatos: CandidataGenerico[];
  /** Lo más grave que dice algún distribuidor del ciclo de vida (null = activo o sin datos). */
  ciclo_vida: {
    estado: Exclude<CicloVida, "activo">;
    distribuidores: string[];
    /** Distribuidores que lo dan por activo (si no todos coinciden). */
    activo_segun?: string[];
    ultima_compra: string | null;
    sustituto: { distribuidor: string; referencia: string | null } | null;
  } | null;
  /** Plazo de fábrica más corto y próximas llegadas (para decidir si esperar sin stock). */
  plazo: {
    dias: number | null;
    distribuidor: string | null;
    llegada: { cantidad: number; fecha: string; distribuidor: string } | null;
    llegada_suficiente: { cantidad: number; fecha: string; distribuidor: string } | null;
  } | null;
};

export type Carrito = {
  id: string;
  distribuidor: string;
  metodo: "api" | "fichero";
  lineas: number;
  total: number | null;
  url: string | null;
  referencia: string | null;
  mensaje: string | null;
  creado_en: string;
};

export type Comparativa = {
  id: string;
  // Sale de una versión de producto O de una lista de compra (los campos del otro van a null).
  version_producto_id: string | null;
  version_nombre: string | null;
  variante_nombre: string | null;
  producto_id: string | null;
  producto_nombre: string | null;
  producto_codigo: string | null;
  lista_compra_id: string | null;
  lista_compra_nombre: string | null;
  /** Cinta para montaje elegida al lanzarla (cm), o null si se lanzó sin ella. */
  cinta_cm: number | null;
  /** Sobreprecio total de la cinta (lo que está puesto ahora). */
  sobreprecio_cinta: number;
  unidades: number;
  ambito_clave: string | null;
  merma_pct: string;
  distribuidores: string[];
  estado: "pendiente" | "en_curso" | "hecha" | "error";
  progreso: number;
  total_lineas: number;
  error: string | null;
  creado_por_nombre: string | null;
  creado_en: string;
  terminada_en: string | null;
  lineas: LineaComparativa[];
  resumen: Record<string, { lineas: number; total: number }>;
  total: number;
  carritos: Carrito[];
};

export type ComparativaResumen = Pick<Comparativa, "id" | "unidades" | "ambito_clave" | "distribuidores" | "estado" | "progreso" | "total_lineas" | "creado_en" | "creado_por_nombre" | "cinta_cm"> & { merma_pct: number };

export type ResultadoCarrito = { id: string; metodo: "api" | "fichero"; url: string; referencia?: string; mensaje?: string; lineas: number; total: number };

// ---------------- Listas de compra (BOMs sueltas, sin producto) ----------------

export type ListaCompraResumen = {
  id: string;
  nombre: string;
  notas: string | null;
  creado_en: string;
  actualizado_en: string;
  creado_por_nombre: string | null;
  num_lineas: number;
  ultima_comparativa: string | null;
};

export type LineaListaCompra = {
  id: string;
  posicion: number;
  mpn: string | null;
  fabricante: string | null;
  descripcion: string | null;
  valor: string | null;
  encapsulado: string | null;
  designadores: string | null;
  order_info: string | null;
  cantidad: number;
  origen: string | null;
};

export type ListaCompra = Omit<ListaCompraResumen, "num_lineas" | "ultima_comparativa"> & {
  lineas: LineaListaCompra[];
  ficheros: { id: string; nombre: string; lineas: number; creado_en: string; creado_por_nombre: string | null }[];
};

// Proyectos de ingeniería (independientes de producto y compras).
export type EstadoProyecto = "activo" | "pausado" | "cerrado";
export type EstadoTarea = "pendiente" | "en_curso" | "hecha";
export type Prioridad = "baja" | "media" | "alta";
export const ESTADOS_PROYECTO: EstadoProyecto[] = ["activo", "pausado", "cerrado"];
export const ESTADOS_TAREA: EstadoTarea[] = ["pendiente", "en_curso", "hecha"];
export const PRIORIDADES: Prioridad[] = ["alta", "media", "baja"];

type ProyectoBase = {
  id: string;
  nombre: string;
  codigo: string | null;
  descripcion: string | null;
  estado: EstadoProyecto;
  responsable_id: string | null;
  responsable_nombre: string | null;
  fecha_inicio: string | null;
  fecha_fin: string | null;
};

export type ProyectoResumen = ProyectoBase & {
  miembros: number;
  pendientes: number;
  en_curso: number;
  hechas: number;
  mis_tareas: number;
  proximo_hito: { nombre: string; fecha: string } | null;
  horas_estimadas: number;
  horas_imputadas: number;
};

export type ProyectoDetalle = ProyectoBase & {
  miembros: { id: string; nombre: string; email: string }[];
  permisos: { gestionar: boolean; editar_tareas: boolean; borrar: boolean };
};

export type Tarea = {
  id: string;
  proyecto_id: string;
  titulo: string;
  descripcion: string | null;
  estado: EstadoTarea;
  prioridad: Prioridad;
  asignado_id: string | null;
  asignado_nombre: string | null;
  fecha_inicio: string | null;
  fecha_limite: string | null;
  orden: number;
  completada_en: string | null;
  creado_en: string;
  horas_estimadas: number | null;
  horas_imputadas: number;
  pasos_total: number;
  pasos_hechos: number;
  comentarios: number;
  adjuntos: number;
};

/** Paso del checklist de una tarea. */
export type PasoTarea = { id: string; texto: string; hecho: boolean; orden: number; hecho_en: string | null; hecho_por_nombre: string | null };
export type ComentarioTarea = { id: string; texto: string; autor_id: string; autor: string; creado_en: string; editado_en: string | null };
export type AdjuntoTarea = {
  id: string;
  nombre: string;
  tamano: number;
  mime: string | null;
  creado_por: string;
  creado_por_nombre: string;
  creado_en: string;
};
export type ColaboracionTarea = { pasos: PasoTarea[]; comentarios: ComentarioTarea[]; adjuntos: AdjuntoTarea[] };

export type Imputacion = {
  id: string;
  tarea_id: string;
  usuario_id: string;
  usuario_nombre: string;
  fecha: string;
  horas: number;
  nota: string | null;
};

export type TareaDetalle = Tarea & { imputaciones: Imputacion[]; cierre_hasta: string | null };

export type Hito = {
  id: string;
  proyecto_id: string;
  nombre: string;
  descripcion: string | null;
  fecha: string;
  completado: boolean;
};

/** Tarea de «mis tareas»: con el proyecto al que pertenece. */
export type MiTarea = Tarea & { proyecto_nombre: string; proyecto_codigo: string | null; proyecto_estado: EstadoProyecto };

/** Hoja de horas semanal del usuario: horas por tarea y día (AAAA-MM-DD → horas). */
export type HojaHoras = {
  desde: string;
  hasta: string;
  filas: (Tarea & { proyecto_nombre: string; proyecto_codigo: string | null; editable: boolean; dias: Record<string, number> })[];
  disponibles: { id: string; titulo: string; proyecto_id: string; proyecto_nombre: string; proyecto_codigo: string | null }[];
  /** Último día con las horas cerradas (no se pueden tocar), o null. */
  cierre_hasta: string | null;
};

/** Cierre de horas vigente e historial de cierres y reaperturas. */
export type CierreHoras = {
  hasta: string | null;
  historial: { id: string; hasta: string | null; nota: string | null; creado_en: string; usuario_nombre: string }[];
};

/** Informe de horas de un periodo (admin: todas; resto: sus proyectos como responsable y sus horas). */
export type InformeHoras = {
  desde: string;
  hasta: string;
  completo: boolean;
  cierre_hasta: string | null;
  total: number;
  personas: Opcion[];
  por_proyecto: {
    proyecto_id: string;
    proyecto_nombre: string;
    proyecto_codigo: string | null;
    horas: number;
    personas: number;
    estimadas: number;
    acumuladas: number;
  }[];
  por_persona: { usuario_id: string; usuario_nombre: string; horas: number; proyectos: Record<string, number> }[];
  por_tarea: {
    tarea_id: string;
    tarea_titulo: string;
    proyecto_id: string;
    proyecto_nombre: string;
    proyecto_codigo: string | null;
    horas: number;
    estado: EstadoTarea | null;
    asignado_nombre: string | null;
    horas_estimadas: number | null;
    horas_totales: number;
  }[];
  detalle: {
    id: string;
    fecha: string;
    horas: number;
    nota: string | null;
    usuario_id: string;
    usuario_nombre: string;
    tarea_id: string;
    tarea_titulo: string;
    proyecto_id: string;
    proyecto_nombre: string;
    proyecto_codigo: string | null;
  }[];
  detalle_truncado: boolean;
};

/** Carga prevista por persona y semana (horas que faltan de sus tareas abiertas frente a sus horas disponibles). */
export type Carga = {
  hoy: string;
  semanas: string[];
  fraccion_semana_actual: number;
  puede_editar_capacidad: boolean;
  personas: {
    usuario_id: string;
    nombre: string;
    horas_semana: number;
    semanas: number[];
    mas_adelante: number;
    sin_fecha: number;
    vencidas: number;
    /** `id` null = tareas de proyectos que no ves (solo las horas). */
    tareas: {
      id: string | null;
      titulo: string | null;
      proyecto_id: string | null;
      proyecto_nombre: string | null;
      proyecto_codigo: string | null;
      fecha_inicio: string | null;
      fecha_limite: string | null;
      restantes: number;
      vencida: boolean;
      semanas: number[];
    }[];
  }[];
};

export type Calendario = {
  hitos: (Hito & { proyecto_nombre: string; proyecto_codigo: string | null })[];
  tareas: (Tarea & { proyecto_nombre: string; proyecto_codigo: string | null })[];
};
